import { ContentBundleSchema, } from "@talent/protocol";
import { BUNDLE_2_0_0 } from "./bundle-2.0.0.js";
import { layOutOptions, mulberry32, seedFrom } from "./random.js";
export * from "./games.js";
export * from "./maze.js";
export * from "./robot.js";
export * from "./sequence.js";
export * from "./shadow.js";
export * from "./mirror.js";
export * from "./parts.js";
export * from "./search.js";
export * from "./code-key.js";
export * from "./tiling.js";
export * from "./key-map.js";
export * from "./difference.js";
export * from "./rule-switch.js";
export * from "./pairs.js";
export * from "./ordering.js";
export * from "./math.js";
export * from "./sudoku.js";
export * from "./letters.js";
export * from "./words.js";
const BUNDLES = [BUNDLE_2_0_0];
/**
 * Bundles are looked up by explicit version, never "latest".
 * Under TWA the web app updates without user action, so a client that asked for
 * "latest" could cross a scoring change mid-trajectory with no trace in the data.
 * See ADR-002 — bundle pinning is the mitigation and this is where it is enforced.
 */
export function getBundle(version) {
    const found = BUNDLES.find((b) => b.bundleVersion === version);
    if (!found)
        throw new Error(`no content bundle for version ${version}`);
    return ContentBundleSchema.parse(found);
}
export function getTask(bundleVersion, taskId) {
    const task = getBundle(bundleVersion).tasks.find((t) => t.id === taskId);
    if (!task)
        throw new Error(`task ${taskId} not in bundle ${bundleVersion}`);
    return task;
}
export function listBundleVersions() {
    return BUNDLES.map((b) => b.bundleVersion);
}
/**
 * The distinct activity families a bundle offers.
 *
 * Families, not tasks. The two A-13 arms of forward span are one family, so counting tasks
 * would report a child as having covered two kinds of activity when they had played the same
 * game twice with a different skin.
 */
export function familiesInBundle(version) {
    return [...new Set(getBundle(version).tasks.map((t) => t.family))];
}
/** Which family a task belongs to, or undefined for a task not in this bundle. */
export function familyOfTask(version, taskId) {
    return getBundle(version).tasks.find((t) => t.id === taskId)?.family;
}
/** Every task id in a family. */
export function taskIdsInFamily(version, family) {
    return getBundle(version)
        .tasks.filter((t) => t.family === family)
        .map((t) => t.id);
}
/** Every objective in a bundle, in catalogue order. */
export function objectivesInBundle(version) {
    return getBundle(version).objectives;
}
export function getObjective(version, objectiveId) {
    const found = getBundle(version).objectives.find((o) => o.id === objectiveId);
    if (!found)
        throw new Error(`objective ${objectiveId} not in bundle ${version}`);
    return found;
}
/**
 * The objectives practised by a level of a task, in catalogue order.
 *
 * Usually one. More than one is legal and means the windows overlap — which is why
 * `task_start` records the objective a run was FOR rather than leaving it to be inferred from
 * the level afterwards (ADR-011).
 */
export function objectivesAtLevel(version, taskId, level) {
    return getBundle(version).objectives.filter((o) => o.steps.some((s) => s.taskId === taskId && level >= s.from && level <= s.to));
}
/**
 * The form id a demonstration draws with.
 *
 * A demonstration needs a stimulus, and it must not be one a scored trial will later use — every
 * generator is seeded from `(formId, trialIndex, difficulty)`, so sharing a form would show the
 * child the answer to a trial they have not taken yet.
 *
 * **It is deliberately NOT in any task's `formIds`.** That array is the set of alternate forms
 * that control practice effects (`[FACT: S14]`), and a demonstration is not one of them — a
 * child who watched a tutorial has not practised an alternate form of anything.
 *
 * So it is allowed past the form check below and excluded from the set — which is exactly the
 * distinction the check was missing, and why the games that validate their form id threw on
 * their own demonstration.
 */
export const DEMO_FORM = "demo";
/** Whether a form id is usable for generation: a real alternate form, or the demonstration. */
const isKnownForm = (task, formId) => formId === DEMO_FORM || task.measurement.formIds.includes(formId);
export function generateSpanStimulus(task, formId, trialIndex, difficulty) {
    if (task.measurement.scoring !== "span") {
        throw new Error(`${task.id} is not a span task`);
    }
    if (!isKnownForm(task, formId)) {
        throw new Error(`form ${formId} is not defined for ${task.id}`);
    }
    const cols = Number(task.params["gridCols"]);
    const rows = Number(task.params["gridRows"]);
    const cells = cols * rows;
    if (difficulty > cells) {
        throw new Error(`difficulty ${difficulty} exceeds ${cells} cells for ${task.id}`);
    }
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    const seq = [];
    while (seq.length < difficulty) {
        const next = Math.floor(rand() * cells);
        if (seq.length === 0 || seq[seq.length - 1] !== next)
            seq.push(next);
    }
    return seq;
}
/** Span scoring: the response must reproduce the sequence in order, exactly. */
export function scoreSpanTrial(stimulus, response) {
    return (stimulus.length === response.length && stimulus.every((v, i) => response[i] === v));
}
/** Canonical form so two patterns can be compared regardless of generation order. */
const canon = (cells) => [...cells].sort((a, b) => a - b).join(",");
/**
 * Pattern-matching stimulus for the visuospatial task.
 *
 * Distractors are near-misses: each is the target with one filled cell moved to an
 * empty one. Two consequences, both deliberate:
 *
 *   - Every option has the SAME number of filled cells, so counting cells is not a
 *     shortcut to the answer. A task solvable by counting would measure something
 *     other than visuospatial discrimination.
 *   - The child has to compare configurations rather than gestalt-reject obviously
 *     different shapes, which is what makes difficulty scale with cell count.
 *
 * Deterministic from (formId, trialIndex, difficulty), like the span generator, so a
 * trial is re-scorable from the event record alone.
 */
export function generatePatternStimulus(task, formId, trialIndex, difficulty) {
    if (task.measurement.scoring !== "accuracy") {
        throw new Error(`${task.id} is not a matching task`);
    }
    if (!isKnownForm(task, formId)) {
        throw new Error(`form ${formId} is not defined for ${task.id}`);
    }
    const cols = Number(task.params["gridCols"]);
    const rows = Number(task.params["gridRows"]);
    const cells = cols * rows;
    const distractors = Number(task.params["distractors"]);
    // One cell must remain empty or a near-miss distractor cannot be built by moving a
    // filled cell, and the task would have no wrong answers to offer.
    if (difficulty < 1 || difficulty >= cells) {
        throw new Error(`difficulty ${difficulty} outside 1..${cells - 1} for ${task.id}`);
    }
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    // Seeded Fisher-Yates over cell indices, then take the first `difficulty`.
    const order = Array.from({ length: cells }, (_, i) => i);
    for (let i = order.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        const a = order[i];
        const b = order[j];
        if (a === undefined || b === undefined)
            continue;
        order[i] = b;
        order[j] = a;
    }
    const target = order.slice(0, difficulty).sort((a, b) => a - b);
    const seen = new Set([canon(target)]);
    const built = [];
    // Bounded: with `difficulty` filled and `cells - difficulty` empty there are
    // plenty of near-misses, but the loop is capped so a pathological grid cannot hang.
    const maxAttempts = 200;
    for (let attempt = 0; attempt < maxAttempts && built.length < distractors; attempt++) {
        const filled = [...target];
        const empty = order.slice(difficulty);
        const dropIdx = Math.floor(rand() * filled.length);
        const addIdx = Math.floor(rand() * empty.length);
        const add = empty[addIdx];
        if (add === undefined)
            continue;
        filled.splice(dropIdx, 1);
        filled.push(add);
        const candidate = filled.sort((a, b) => a - b);
        const key = canon(candidate);
        if (seen.has(key))
            continue;
        seen.add(key);
        built.push(candidate);
    }
    if (built.length < distractors) {
        throw new Error(`could not build ${distractors} distinct distractors for ${task.id} at difficulty ${difficulty}`);
    }
    // The whole row is laid out together, so no slot carries anything — not merely the target
    // placed at a seeded position, which leaves the distractors in the order the loop built them.
    const { options, correctIndex } = layOutOptions(target, built, rand);
    return { target, options, correctIndex };
}
/** Matching scoring: the chosen option must be the target. */
export function scorePatternTrial(correctIndex, chosenIndex) {
    return correctIndex === chosenIndex;
}
export { BUNDLE_2_0_0 };
//# sourceMappingURL=index.js.map